// src/stock/AjoutStock.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Form, Button, Spinner, InputGroup
} from "react-bootstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function AjoutStock() {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const typeDefaut = queryParams.get('type') || 'entree';

    const [produit, setProduit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const [mouvement, setMouvement] = useState({
        type: typeDefaut,
        quantite: 1,
        motif: '',
        reference: ''
    });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchProduit();
    }, [id]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchProduit = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setProduit(result.data);
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    /* ============================================================
       HANDLERS
       ============================================================ */
    const resetForm = () => {
        setMouvement({
            type: 'entree',
            quantite: 1,
            motif: '',
            reference: ''
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (mouvement.quantite <= 0) {
            showToast('error', 'Champ invalide', "La quantité doit être supérieure à 0");
            return;
        }

        if (mouvement.type === 'sortie' && mouvement.quantite > (produit.stock_actuel || 0)) {
            showToast('error', 'Stock insuffisant', `Stock actuel : ${produit.stock_actuel}`);
            return;
        }

        setSaving(true);

        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;

            const response = await fetch(
                `${API_BASE_URL}/mouvements_stock.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id_produit: id,
                        type: mouvement.type,
                        quantite: mouvement.quantite,
                        motif: mouvement.motif,
                        reference: mouvement.reference,
                        id_utilisateur: user?.id || user?.id_utilisateur || 1
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                showToast('success', 'Mouvement enregistré',
                    mouvement.type === 'entree'
                        ? `+${mouvement.quantite} ${produit.unite_mesure} ajoutés au stock`
                        : `−${mouvement.quantite} ${produit.unite_mesure} retirés du stock`
                );
                setTimeout(() => navigate(`/stock/produits/${id}`), 1500);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de l'enregistrement");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    /* ============================================================
       LOADING — Bootstrap spinner (conservé)
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <Spinner
                                animation="border"
                                role="status"
                                className="mb-3"
                                style={{ width: '3rem', height: '3rem', color: COLORS.primary }}
                            >
                                <span className="visually-hidden">Chargement...</span>
                            </Spinner>
                            <h5 className="text-muted">Chargement du produit...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (!produit) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="empty-state-charte">
                        <div className="empty-state-icon-charte" style={{ background: 'rgba(239,68,68,0.1)', color: COLORS.danger }}>
                            <i className="ri-error-warning-line"></i>
                        </div>
                        <h6 className="fw-semibold mb-1">Produit non trouvé</h6>
                        <p className="text-muted small mb-3">Le produit que vous recherchez n'existe pas</p>
                        <Button className="btn-modern-outline" onClick={() => navigate('/stock/produits')}>
                            <i className="ri-arrow-left-line me-1"></i>Retour
                        </Button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const isEntree = mouvement.type === 'entree';

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper" style={{
                                background: isEntree
                                    ? 'linear-gradient(135deg, #10b981, #059669)'
                                    : 'linear-gradient(135deg, #f59e0b, #d97706)'
                            }}>
                                <i className={`ri-${isEntree ? 'add-box-line' : 'subtract-line'}`}></i>
                            </div>
                            <div>
                                <h5 className="header-title">
                                    {isEntree ? 'Ajouter au stock' : 'Retirer du stock'}
                                </h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-suitcase-2-line"></i> {produit.designation}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-barcode-line"></i> {produit.reference}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate(`/stock/produits/${id}`)}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/stock/mouvements')}>
                                <i className="ri-history-line"></i> Historique
                            </Button>
                        </div>
                    </div>

                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="card-header-gradient" style={{
                                    background: isEntree
                                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                        : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                }}>
                                    <h5 className="mb-0 text-white d-flex align-items-center">
                                        <i className="ri-information-line me-2"></i>
                                        {isEntree ? 'Entrée de stock' : 'Sortie de stock'}
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">

                                    {/* ===== CARTE PRODUIT ===== */}
                                    <div className="product-card-charte mb-4">
                                        <div className="product-avatar-charte">
                                            <i className="ri-suitcase-2-line"></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <h5 className="mb-1 fw-bold">{produit.designation}</h5>
                                            <div className="d-flex gap-3 flex-wrap align-items-center">
                                                <span className="reference-badge-charte">
                                                    <i className="ri-barcode-line me-1"></i>
                                                    {produit.reference}
                                                </span>
                                                <span className={`stock-pill-charte ${(produit.stock_actuel || 0) <= (produit.seuil_alerte || 0) ? 'warning' : 'normal'}`}>
                                                    <i className="ri-stack-line me-1"></i>
                                                    Stock actuel : {produit.stock_actuel || 0} {produit.unite_mesure}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-4">

                                            {/* SECTION 1 : Type de mouvement */}
                                            <Col md={12}>
                                                <div className="section-title-charte">
                                                    <span className="section-icon-charte"
                                                          style={{
                                                              background: isEntree ? `${COLORS.success}15` : `${COLORS.warning}15`,
                                                              color: isEntree ? COLORS.success : COLORS.warning
                                                          }}>
                                                        <i className="ri-exchange-line"></i>
                                                    </span>
                                                    <h6>Type de mouvement</h6>
                                                </div>

                                                <div className="d-flex gap-3 flex-wrap">
                                                    <div
                                                        className={`type-option-charte ${mouvement.type === 'entree' ? 'active-entree' : ''}`}
                                                        onClick={() => setMouvement({ ...mouvement, type: 'entree' })}
                                                    >
                                                        <div className="type-option-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                            <i className="ri-add-box-line"></i>
                                                        </div>
                                                        <div className="type-option-content-charte">
                                                            <div className="type-option-title-charte">Entrée</div>
                                                            <div className="type-option-subtitle-charte">Ajout au stock</div>
                                                        </div>
                                                        {mouvement.type === 'entree' && (
                                                            <i className="ri-checkbox-circle-fill type-option-check-charte" style={{ color: COLORS.success }}></i>
                                                        )}
                                                    </div>

                                                    <div
                                                        className={`type-option-charte ${mouvement.type === 'sortie' ? 'active-sortie' : ''}`}
                                                        onClick={() => setMouvement({ ...mouvement, type: 'sortie' })}
                                                    >
                                                        <div className="type-option-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                                            <i className="ri-subtract-line"></i>
                                                        </div>
                                                        <div className="type-option-content-charte">
                                                            <div className="type-option-title-charte">Sortie</div>
                                                            <div className="type-option-subtitle-charte">Retrait du stock</div>
                                                        </div>
                                                        {mouvement.type === 'sortie' && (
                                                            <i className="ri-checkbox-circle-fill type-option-check-charte" style={{ color: COLORS.warning }}></i>
                                                        )}
                                                    </div>
                                                </div>
                                            </Col>

                                            {/* SECTION 2 : Détails du mouvement */}
                                            <Col md={12}>
                                                <div className="section-title-charte mt-2">
                                                    <span className="section-icon-charte"
                                                          style={{
                                                              background: isEntree ? `${COLORS.primary}15` : `${COLORS.warning}15`,
                                                              color: isEntree ? COLORS.primary : COLORS.warning
                                                          }}>
                                                        <i className="ri-file-list-line"></i>
                                                    </span>
                                                    <h6>Détails du mouvement</h6>
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Quantité <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text>
                                                            <i className="ri-hashtag"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="number"
                                                            min="1"
                                                            value={mouvement.quantite}
                                                            onChange={(e) => setMouvement({ ...mouvement, quantite: parseInt(e.target.value) || 1 })}
                                                            required
                                                        />
                                                        <InputGroup.Text className="bg-light">
                                                            {produit.unite_mesure}
                                                        </InputGroup.Text>
                                                    </InputGroup>
                                                    {mouvement.type === 'sortie' && (
                                                        <Form.Text className="text-muted small">
                                                            Maximum disponible : {produit.stock_actuel || 0} {produit.unite_mesure}
                                                        </Form.Text>
                                                    )}
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Unité de mesure</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text>
                                                            <i className="ri-scales-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={produit.unite_mesure}
                                                            disabled
                                                            className="bg-light"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Référence <span className="text-muted small">(optionnel)</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text>
                                                            <i className="ri-file-text-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={mouvement.reference}
                                                            onChange={(e) => setMouvement({ ...mouvement, reference: e.target.value })}
                                                            placeholder="N° BL, N° facture, etc."
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Motif / Observations
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text>
                                                            <i className="ri-chat-1-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            value={mouvement.motif}
                                                            onChange={(e) => setMouvement({ ...mouvement, motif: e.target.value })}
                                                            placeholder="Raison du mouvement..."
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            {/* SECTION 3 : Actions */}
                                            <Col md={12}>
                                                <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
                                                <div className="d-flex justify-content-end gap-3 flex-wrap">
                                                    <Button
                                                        type="button"
                                                        className="btn-modern-outline"
                                                        onClick={resetForm}
                                                    >
                                                        <i className="ri-refresh-line me-1"></i> Réinitialiser
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        className="btn-modern-outline"
                                                        onClick={() => navigate(`/stock/produits/${id}`)}
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        className={isEntree ? 'btn-modern-success' : 'btn-modern-warning'}
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <><Spinner size="sm" className="me-2" />Enregistrement...</>
                                                        ) : (
                                                            <>
                                                                <i className={`ri-${isEntree ? 'add-box-line' : 'subtract-line'} me-2`}></i>
                                                                {isEntree ? 'Ajouter au stock' : 'Retirer du stock'}
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>

                            {/* ===== INFO CARD ===== */}
                            <Card className="border-0 shadow-sm rounded-3 mt-4">
                                <Card.Body className="p-4">
                                    <div className="info-banner-charte">
                                        <div className="info-banner-icon-charte"
                                             style={{
                                                 background: isEntree ? `${COLORS.success}15` : `${COLORS.warning}15`,
                                                 color: isEntree ? COLORS.success : COLORS.warning
                                             }}>
                                            <i className={isEntree ? 'ri-check-line' : 'ri-alert-line'}></i>
                                        </div>
                                        <div>
                                            <h6 className="fw-semibold mb-1">
                                                {isEntree ? 'Ajout de stock' : 'Retrait de stock'}
                                            </h6>
                                            <p className="small text-muted mb-0">
                                                {isEntree ? (
                                                    <>
                                                        <strong>Entrée :</strong> augmente la quantité disponible.
                                                        Généralement utilisé pour les réceptions de commandes,
                                                        les retours clients ou les inventaires positifs.
                                                    </>
                                                ) : (
                                                    <>
                                                        <strong>Sortie :</strong> diminue la quantité disponible.
                                                        Généralement utilisé pour les ventes, les pertes ou les
                                                        inventaires négatifs.
                                                    </>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }

                /* ===== SECTION ===== */
                .section-title-charte {
                    display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 1rem; padding-bottom: 0.5rem;
                    border-bottom: 2px solid #f1f5f9;
                }
                .section-title-charte h6 {
                    margin: 0; font-weight: 600;
                    font-size: 0.95rem; color: #1e293b;
                }
                .section-icon-charte {
                    width: 34px; height: 34px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    font-size: 1rem;
                }

                /* ===== FORMULAIRES ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }

                /* ===== PRODUCT CARD ===== */
                .product-card-charte {
                    display: flex;
                    align-items: center;
                    gap: 1.25rem;
                    padding: 1.25rem;
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s ease;
                }
                .product-card-charte:hover {
                    box-shadow: 0 8px 20px rgba(0,0,0,0.04);
                    border-color: var(--secondary);
                }
                .product-avatar-charte {
                    width: 60px;
                    height: 60px;
                    background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--primary);
                    font-size: 1.6rem;
                    flex-shrink: 0;
                }

                /* ===== REFERENCE BADGE ===== */
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }

                /* ===== STOCK PILL ===== */
                .stock-pill-charte {
                    display: inline-flex; align-items: center;
                    padding: 0.35rem 0.8rem;
                    border-radius: 50px;
                    font-weight: 600;
                    font-size: 0.75rem;
                }
                .stock-pill-charte.normal {
                    background: ${COLORS.success}15;
                    color: var(--success);
                }
                .stock-pill-charte.warning {
                    background: ${COLORS.warning}15;
                    color: var(--warning);
                }

                /* ===== TYPE OPTIONS ===== */
                .type-option-charte {
                    flex: 1;
                    min-width: 200px;
                    display: flex;
                    align-items: center;
                    gap: 0.85rem;
                    padding: 1rem 1.25rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 14px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    background: white;
                    position: relative;
                }
                .type-option-charte:hover {
                    border-color: var(--secondary);
                    background: #f8fafc;
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0,0,0,0.05);
                }
                .type-option-charte.active-entree {
                    border-color: var(--success);
                    background: ${COLORS.success}08;
                    box-shadow: 0 6px 16px rgba(16,185,129,0.15);
                }
                .type-option-charte.active-sortie {
                    border-color: var(--warning);
                    background: ${COLORS.warning}08;
                    box-shadow: 0 6px 16px rgba(245,158,11,0.15);
                }
                .type-option-icon-charte {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.3rem;
                    flex-shrink: 0;
                }
                .type-option-content-charte { flex: 1; min-width: 0; }
                .type-option-title-charte {
                    font-weight: 700;
                    color: #1e293b;
                    font-size: 0.95rem;
                }
                .type-option-subtitle-charte {
                    color: #94a3b8;
                    font-size: 0.75rem;
                }
                .type-option-check-charte {
                    font-size: 1.3rem;
                    flex-shrink: 0;
                }

                /* ===== INFO BANNER ===== */
                .info-banner-charte {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }
                .info-banner-icon-charte {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.3rem;
                    flex-shrink: 0;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-success:disabled,
                .btn-modern-warning:disabled { opacity: 0.6; transform: none; }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .type-option-charte { min-width: 100%; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}